import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Divider,
    Grid,
    CircularProgress,
    IconButton
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
    SaveRounded,
    ClearRounded,
    AddCircle,
    Delete
} from "@mui/icons-material";

import CustomButton from "../../../components/common/CustomButton";
import CustomTextField from "../../../components/common/CustomTextField";
import CustomTextarea from "../../../components/common/CustomTextarea";
import CustomAutocomplete from "../../../components/common/CustomAutoComplete";

import {
    CASE_STUDY_VIEW,
    CASE_STUDY_UPDATE
} from "../../../services/apiRoutes";

import {
    Editor
} from "react-draft-wysiwyg";

import {
    EditorState,
    convertFromHTML,
    ContentState,
    convertToRaw
} from "draft-js";

import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import CustomStyle from "../../../components/common/CustomStyle";


const ValidationSchema = Yup.object().shape({
    title: Yup.string().required("Title required"),
    category: Yup.string().required("Category required"),
    metaTitle: Yup.string().required("Meta title required"),
    metaDescription: Yup.string().required("Meta description required"),
    views: Yup.number().required("Views required"),
    publishedAt: Yup.string().required("Published date required"),
    status: Yup.string().required("Status required"),
    particulars: Yup.array().of(
        Yup.object().shape({
            sectionHeading: Yup.string().required("Heading required"),
            sectionContent: Yup.string().required("Content required")
        })
    )
});

const UpdateCaseStudy = ({
    handleClose,
    caseStudyId,
    setMessage,
    getCaseStudyList,
    blogCategory,
    blogStatus
}) => {

    const [loading, setLoading] = useState(true);
    const [initialValues, setInitialValues] = useState(null);

    const [editorState, setEditorState] =
        useState(EditorState.createEmpty());

    const [sectionEditors, setSectionEditors] = useState([]);



    const htmlToEditor = (html) => {
        const blocks = convertFromHTML(html || "");
        const state = ContentState.createFromBlockArray(
            blocks.contentBlocks,
            blocks.entityMap
        );

        return EditorState.createWithContent(state);
    };


    useEffect(() => {
        fetchCaseStudy();
    }, []);


    const fetchCaseStudy = async () => {
        try {
            const token = sessionStorage.getItem("accessToken");

            const res = await axios.get(
                CASE_STUDY_VIEW(caseStudyId),
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data = res.data.caseStudy;

            setInitialValues({
                title: data.title || "",
                featuredImage: data.featuredImage || "",
                category: Number(data.category),
                metaTitle: data.metaTitle || "",
                metaDescription: data.metaDescription || "",
                views: data.views || 0,
                publishedAt: data.publishedAt?.split("T")[0] || "",
                status: Number(data.status),
                particulars:
                    data.particulars?.length > 0
                        ? data.particulars
                        : [
                            {
                                sectionHeading: "",
                                sectionContent: "",
                                sectionImage: null
                            }
                        ]
            });

            setEditorState(
                htmlToEditor(data.content)
            );

            setSectionEditors(
                data.particulars.map((x) =>
                    htmlToEditor(x.sectionContent)
                )
            );

        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };



    const handleFeaturedImage = (
        e,
        setFieldValue
    ) => {
        const file = e.target.files[0];
        if (file)
            setFieldValue("featuredImage", file);
    };


    const handleSectionImage = (
        e,
        index,
        setFieldValue
    ) => {
        const file = e.target.files[0];

        if (file) {
            setFieldValue(
                `particulars.${index}.sectionImage`,
                file
            );
        }
    };


    const handleSectionEditor = (
        index,
        state,
        setFieldValue
    ) => {
        const updated = [...sectionEditors];
        updated[index] = state;
        setSectionEditors(updated);

        const html = draftToHtml(
            convertToRaw(
                state.getCurrentContent()
            )
        );

        setFieldValue(
            `particulars.${index}.sectionContent`,
            html
        );
    };



    const handleSubmit = async (
        values,
        { setSubmitting }
    ) => {
        try {
            const token =
                sessionStorage.getItem("accessToken");

            const formData = new FormData();

            formData.append(
                "title",
                values.title
            );

            formData.append(
                "content",
                draftToHtml(
                    convertToRaw(
                        editorState.getCurrentContent()
                    )
                )
            );

            formData.append(
                "category",
                values.category
            );

            formData.append(
                "metaTitle",
                values.metaTitle
            );

            formData.append(
                "metaDescription",
                values.metaDescription
            );

            formData.append(
                "views",
                values.views
            );

            formData.append(
                "publishedAt",
                values.publishedAt
            );

            formData.append(
                "status",
                values.status
            );

            if (
                values.featuredImage instanceof File
            ) {
                formData.append(
                    "featuredImage",
                    values.featuredImage
                );
            }


            values.particulars.forEach(
                (item, index) => {
                    formData.append(
                        `particulars[${index}][sectionHeading]`,
                        item.sectionHeading
                    );

                    formData.append(
                        `particulars[${index}][sectionContent]`,
                        item.sectionContent
                    );

                    if (
                        item.sectionImage instanceof File
                    ) {
                        formData.append(
                            `particulars[${index}][sectionImage]`,
                            item.sectionImage
                        );
                    } else {
                        formData.append(
                            `particulars[${index}][sectionImage]`,
                            item.sectionImage || ""
                        );
                    }
                }
            );

            const response =
                await axios.put(
                    CASE_STUDY_UPDATE(caseStudyId),
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type":
                                "multipart/form-data"
                        }
                    }
                );

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text:
                        "Case Study Updated Successfully",
                    type: "success"
                });

                getCaseStudyList();

                setTimeout(() => {
                    handleClose();
                }, 1000);
            }

        } catch (error) {
            console.log(error);

            setMessage({
                open: true,
                text: "Update failed",
                type: "error"
            });
        } finally {
            setSubmitting(false);
        }
    };


    if (loading) return <p>Loading...</p>;



    return (
        <Box>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    setFieldValue,
                    isSubmitting
                }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={6} md={6}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    Update Case Study
                                </Typography>
                            </Grid>
                            <Grid item xs={6} md={6} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container spacing={1} style={{ paddingLeft: "5%", paddingRight: "5%", paddingTop: '5px' }}>
                            <Grid item xs={6}>
                                <CustomTextarea
                                    name="title"
                                    fieldLabel="Title"
                                    value={values.title}
                                    onChange={handleChange}
                                />
                            </Grid>


                            <Grid item xs={6}>
                                <CustomAutocomplete
                                    options={blogCategory}
                                    fieldLabel="Category"
                                    getOptionLabel={(o) =>
                                        o.title
                                    }
                                    value={
                                        blogCategory.find(
                                            (x) =>
                                                x.id ===
                                                values.category
                                        ) || null
                                    }
                                    onChange={(
                                        e,
                                        val
                                    ) =>
                                        setFieldValue(
                                            "category",
                                            val?.id
                                        )
                                    }
                                />
                            </Grid>


                            <Grid item xs={12} md={6}>
                                <CustomTextField
                                    type="date"
                                    name="publishedAt"
                                    fieldLabel="Published Date"
                                    required
                                    value={values.publishedAt}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    error={touched.publishedAt && !!errors.publishedAt}
                                    helperText={touched.publishedAt && errors.publishedAt}
                                />
                            </Grid>

                            {/* Status */}
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={blogStatus}
                                    fieldLabel="Status"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={blogStatus.find((option) => option.id === values.status) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('status', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select status"
                                    error={touched.status && !!errors.status}
                                    helperText={touched.status && errors.status}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2
                                    }}
                                >
                                    <input
                                        type="file"
                                        onChange={(e) =>
                                            handleFeaturedImage(
                                                e,
                                                setFieldValue
                                            )
                                        }
                                        style={{
                                            border: "1px solid #CED4DA",
                                            padding: "8px",
                                            borderRadius: "4px",
                                            width: "250px"
                                        }}
                                    />

                                    {typeof values.featuredImage === "string" && (
                                        <img
                                            src={values.featuredImage}
                                            alt="featured"
                                            style={{
                                                width: "100px",
                                                height: "70px",
                                                objectFit: "cover",
                                                borderRadius: "6px",
                                                border: "1px solid #ddd"
                                            }}
                                        />
                                    )}
                                </Box>
                            </Grid>


                            <Grid item xs={12}>
                                <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>
                                    Main Content
                                </Typography>

                                <Editor
                                    editorState={editorState}
                                    onEditorStateChange={setEditorState}
                                    wrapperClassName="demo-wrapper"
                                    editorClassName="demo-editor"
                                    wrapperStyle={{
                                        border: '1px solid #CED4DA', borderRadius: '4px',
                                    }}
                                    editorStyle={{ padding: "10px" }}
                                    toolbar={{
                                        options: [
                                            'inline',
                                            'blockType',
                                            'fontSize',
                                            'fontFamily',
                                            'list',
                                            'textAlign',
                                            'colorPicker',
                                            'link',
                                            'emoji',
                                            'remove',
                                            'history'
                                        ],
                                        inline: {
                                            inDropdown: false,
                                            options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
                                        },
                                        blockType: {
                                            inDropdown: true,
                                            options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                                        },
                                        fontSize: {
                                            options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                                        },
                                        list: {
                                            inDropdown: false,
                                            options: ['unordered', 'ordered', 'indent', 'outdent'],
                                        },
                                        textAlign: {
                                            inDropdown: false,
                                            options: ['left', 'center', 'right', 'justify'],
                                        },
                                        link: {
                                            inDropdown: false,
                                            options: ['link', 'unlink'],
                                        },
                                        history: {
                                            inDropdown: false,
                                            options: ['undo', 'redo'],
                                        },
                                    }}
                                />
                            </Grid>


                            <FieldArray name="particulars">
                                {({ push, remove }) => (
                                    <>
                                        {/* Header */}
                                        <Grid item xs={12}>
                                            <Box
                                                sx={{
                                                    mt: 3,
                                                    mb: 2,
                                                    p: 2,
                                                    borderRadius: "10px",
                                                    background: "#F8F9FA",
                                                    border: "1px solid #DEE2E6",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: "18px",
                                                        color: "#343A40"
                                                    }}
                                                >
                                                    Section Details
                                                </Typography>

                                                <CustomButton
                                                    startIcon={<AddCircle />}
                                                    title="Add Section"
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => {
                                                        push({
                                                            sectionHeading: "",
                                                            sectionContent: "",
                                                            sectionImage: null
                                                        });

                                                        setSectionEditors([
                                                            ...sectionEditors,
                                                            EditorState.createEmpty()
                                                        ]);
                                                    }}
                                                />
                                            </Box>
                                        </Grid>

                                        {values.particulars.map((item, index) => (
                                            <Grid item xs={12} key={index}>
                                                <Box
                                                    sx={{
                                                        p: 3,
                                                        borderRadius: "12px",
                                                        border: "1px solid #E9ECEF",
                                                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                                        background: "#fff",
                                                        mb: 3
                                                    }}
                                                >
                                                    {/* section header */}
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            mb: 3
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 700,
                                                                fontSize: "16px",
                                                                color: "#1976d2"
                                                            }}
                                                        >
                                                            Section #{index + 1}
                                                        </Typography>

                                                        <IconButton
                                                            color="error"
                                                            onClick={() => {
                                                                remove(index);

                                                                const updated = [...sectionEditors];
                                                                updated.splice(index, 1);
                                                                setSectionEditors(updated);
                                                            }}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Box>

                                                    <Grid container spacing={3}>
                                                        {/* heading */}
                                                        <Grid item xs={12} md={6}>
                                                            <CustomTextarea
                                                                name={`particulars.${index}.sectionHeading`}
                                                                fieldLabel="Section Heading"
                                                                value={item.sectionHeading}
                                                                onChange={handleChange}
                                                                placeholder="Enter section heading"
                                                            />
                                                        </Grid>

                                                        {/* image */}
                                                        <Grid item xs={12} md={6}>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: "13px",
                                                                    mb: 1
                                                                }}
                                                            >
                                                                Section Image
                                                            </Typography>

                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 2
                                                                }}
                                                            >
                                                                <input
                                                                    type="file"
                                                                    onChange={(e) =>
                                                                        handleSectionImage(
                                                                            e,
                                                                            index,
                                                                            setFieldValue
                                                                        )
                                                                    }
                                                                    style={{
                                                                        border: "1px solid #CED4DA",
                                                                        padding: "8px",
                                                                        borderRadius: "6px",
                                                                        width: "220px"
                                                                    }}
                                                                />

                                                                {typeof item.sectionImage === "string" && (
                                                                    <img
                                                                        src={item.sectionImage}
                                                                        alt=""
                                                                        style={{
                                                                            width: "90px",
                                                                            height: "70px",
                                                                            objectFit: "cover",
                                                                            borderRadius: "8px",
                                                                            border: "1px solid #ddd"
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Grid>

                                                        {/* editor */}
                                                        <Grid item xs={12}>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: "13px",
                                                                    mb: 1
                                                                }}
                                                            >
                                                                Section Content
                                                            </Typography>

                                                            <Box>


                                                                <Editor
                                                                    editorState={
                                                                        sectionEditors[index]
                                                                    }
                                                                    onEditorStateChange={(state) =>
                                                                        handleSectionEditor(
                                                                            index,
                                                                            state,
                                                                            setFieldValue
                                                                        )
                                                                    }
                                                                    editorStyle={{
                                                                        minHeight: "150px",
                                                                        padding: "10px"
                                                                    }}
                                                                    wrapperClassName="demo-wrapper"
                                                                    editorClassName="demo-editor"
                                                                    wrapperStyle={{
                                                                        border: '1px solid #CED4DA', borderRadius: '4px',
                                                                    }}
                                                                    toolbar={{
                                                                        options: [
                                                                            'inline',
                                                                            'blockType',
                                                                            'fontSize',
                                                                            'fontFamily',
                                                                            'list',
                                                                            'textAlign',
                                                                            'colorPicker',
                                                                            'link',
                                                                            'emoji',
                                                                            'remove',
                                                                            'history'
                                                                        ],
                                                                        inline: {
                                                                            inDropdown: false,
                                                                            options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
                                                                        },
                                                                        blockType: {
                                                                            inDropdown: true,
                                                                            options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                                                                        },
                                                                        fontSize: {
                                                                            options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                                                                        },
                                                                        list: {
                                                                            inDropdown: false,
                                                                            options: ['unordered', 'ordered', 'indent', 'outdent'],
                                                                        },
                                                                        textAlign: {
                                                                            inDropdown: false,
                                                                            options: ['left', 'center', 'right', 'justify'],
                                                                        },
                                                                        link: {
                                                                            inDropdown: false,
                                                                            options: ['link', 'unlink'],
                                                                        },
                                                                        history: {
                                                                            inDropdown: false,
                                                                            options: ['undo', 'redo'],
                                                                        },
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </>
                                )}
                            </FieldArray>


                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UpdateCaseStudy;